import { IDatasetTypeMetadata, IDynamicDatasetTypeMetadata } from 'app/extensibility/metadatas/resource-type/dataset-type-metadata';
import { GlobalVariables } from 'app/services/globals';

import { adfModelTypeMap } from '../consts/adf-model-type-map-const';
import { partitionOptionConst, partitionOptions, preCopyScriptMetadata } from '../consts/metadata-consts';
import { ColumnType, defaultSupportedColumnTypes } from 'app/shared/utils/column-type.util';
import { compact } from 'lodash';
import { getServiceInstance } from 'app/multi-contexts/modules/root/root.application';
import { ExposureControlService } from 'app/services/exposure-control/base-exposure-control.service';
import { format } from 'app/shared/utils/string.util';

export const teradataPartitionOptions = [
    partitionOptionConst.none,
    partitionOptionConst.dyamicRange,
    partitionOptionConst.hash
];

export const teradataTableMetadata: IDynamicDatasetTypeMetadata = async (injector) => {
    const exposureControlService = await getServiceInstance<ExposureControlService>(
        injector,
        'ExposureControlService'
    );
    const metadata: IDatasetTypeMetadata = {
        type: adfModelTypeMap.teradata.datasetType,
        displayName: stringResources.Teradata_DisplayText,
        supportLinkedServiceTypes: [adfModelTypeMap.teradata.linkedServiceType],
        isSource: true,
        isSink: () => __IS_FABRIC_DPE__ || exposureControlService.enableTeradataSinkInADF,
        useExternalCommandWhenSink: true,
        datasetKind: 'table',
        icon: {
            className: 'svg-i-100x100-Teradata'
        },
        showWarningMessage: '{ linkedService && linkedService.version !== "v2" }',
        warningMessage: format(
            stringResources.Dataset_Legacy_IsNotSupportedWarning_alt3,
            stringResources.VersionLabel.toLocaleLowerCase()
        ),
        warningMessageHyperlink: 'https://learn.microsoft.com/azure/data-factory/connector-teradata',
        isFirstClass: true,
        queryTooltipLink: 'https://aka.ms/teradatacopysource',
        category: ['Database'],
        supportCreatedAsV2: true,
        notSupportPhysicalType: '{ linkedService && linkedService.version !== "v2" }',
        splitTablePath: true,
        schemaAlias: 'database',
        supportParallelRead: true,
        supportDataTypes: defaultSupportedColumnTypes.concat(compact([ColumnType.Date, ColumnType.Time])),
        copyJob: {
            multipleQuery: true,
            sourcePartition: {
                supportSourcePartition: false,
                onlyInteger: true
            }
        },
        copySourceControls: [
            {
                type: 'parameterizable',
                child: {
                    type: 'textbox-number',
                    inputType: 'number',
                    bind: 'queryTimeout',
                    label: stringResources.ModelName_Source_QueryTimeout_Min,
                    description: stringResources.QueryTimeout_DescriptionMessage
                },
                dynamicContentTextClass: 'input-spaced',
                hide: () => !GlobalVariables.enableTeradataQueryTimeout
            },
            {
                type: 'radio-switch',
                label: stringResources.ModelName_Partition_Option,
                defaultValue: partitionOptionConst.none,
                description: '<a target="_blank" href="https://aka.ms/teradatacopysource">' + stringResources.LearnMore + '</a>',
                escapeDescription: false,
                options: partitionOptions.filter((p) => teradataPartitionOptions.indexOf(p.value) >= 0),
                bind: 'partitionOption',
                isParameterizable: true,
                cases: [
                    {
                        caseValue: partitionOptionConst.dyamicRange,
                        children: [
                            {
                                type: 'parameterizable',
                                child: {
                                    type: 'columns-selection',
                                    bind: 'partitionColumnName',
                                    label: stringResources.ModelName_Partition_ColumnName,
                                    multiple: false,
                                    includeCopySourceProperties: true,
                                    excludeSourceProperties: ['partitionOption', 'partitionSettings']
                                },
                                dynamicContentTextClass: 'input-spaced'
                            },
                            {
                                type: 'parameterizable',
                                child: {
                                    type: 'textbox',
                                    bind: 'partitionUpperBound',
                                    label: stringResources.ModelName_Partition_UpperBound
                                },
                                dynamicContentTextClass: 'input-spaced'
                            },
                            {
                                type: 'parameterizable',
                                child: {
                                    type: 'textbox',
                                    bind: 'partitionLowerBound',
                                    label: stringResources.ModelName_Partition_LowerBound
                                },
                                dynamicContentTextClass: 'input-spaced'
                            }
                        ]
                    },
                    {
                        caseValue: partitionOptionConst.hash,
                        children: [
                            {
                                type: 'parameterizable',
                                child: {
                                    type: 'columns-selection',
                                    bind: 'partitionColumnName',
                                    label: stringResources.ModelName_Partition_ColumnName,
                                    includeCopySourceProperties: true,
                                    multiple: false,
                                    excludeSourceProperties: ['partitionOption', 'partitionSettings']
                                },
                                dynamicContentTextClass: 'input-spaced'
                            }
                        ]
                    }
                ]
            }
        ],
        copySinkControls: () => (__IS_FABRIC_DPE__ || exposureControlService.enableTeradataSinkInADF) ? compact([
            GlobalVariables.enableTeradataV2PCS ? preCopyScriptMetadata() : undefined,
            {
                type: 'import-export-settings',
                bind: 'importSettings',
                connectionName: stringResources.Teradata_DisplayText,
                formatOptionsLink: '', // TODO: add link
                supportCopyOptions: false
            }
        ]) : undefined
    };

    return metadata;
};
